/**
 * StatusBadge Component
 * 
 * A badge component for displaying status indicators.
 */

import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface StatusBadgeProps {
  /** Badge label */
  label: string;
  /** Status type */
  status?: StatusType;
  /** Whether to show the dot indicator */
  showDot?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const statusColors: Record<StatusType, { bg: string; text: string; dot: string }> = {
  success: {
    bg: colors.success.light,
    text: colors.success.dark,
    dot: colors.success.main,
  },
  warning: {
    bg: colors.warning.light,
    text: colors.warning.dark,
    dot: colors.warning.main,
  },
  error: {
    bg: colors.error.light,
    text: colors.error.dark,
    dot: colors.error.main,
  },
  info: {
    bg: colors.info.light,
    text: colors.info.dark,
    dot: colors.info.main,
  },
  neutral: {
    bg: colors.neutral[100],
    text: colors.neutral[700],
    dot: colors.neutral[500],
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  label,
  status = 'neutral',
  showDot = true,
  size = 'medium',
  sx,
}) => {
  const colorScheme = statusColors[status];
  const isSmall = size === 'small';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: isSmall ? 1.5 : 2,
        py: isSmall ? 0.25 : 0.5,
        borderRadius: `${radii.full}px`,
        backgroundColor: colorScheme.bg,
        ...sx,
      }}
    >
      {showDot && (
        <Box
          sx={{
            width: isSmall ? 6 : 8,
            height: isSmall ? 6 : 8,
            borderRadius: '50%',
            backgroundColor: colorScheme.dot,
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: isSmall ? typographyTokens.fontSize.xs : typographyTokens.fontSize.sm,
          fontWeight: typographyTokens.fontWeight.medium,
          color: colorScheme.text,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default StatusBadge;

